export function timeAwareGreeting(timezone: string, firstName?: string | null): string {
  let hour = 9;
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    }).formatToParts(new Date());
    const h = parts.find((p) => p.type === 'hour')?.value;
    if (h) hour = parseInt(h, 10);
  } catch {
    /* fall through to default */
  }
  let greeting = 'Good evening';
  if (hour < 12) greeting = 'Good morning';
  else if (hour < 17) greeting = 'Good afternoon';
  return firstName ? `${greeting}, ${firstName}` : greeting;
}

export function dayOfWeekIn(timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-AU', { timeZone: timezone, weekday: 'long' }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-AU', { weekday: 'long' }).format(new Date());
  }
}

export function initials(fullName?: string | null): string {
  if (!fullName) return '·';
  const parts = fullName.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '·';
}

export function firstName(fullName?: string | null): string {
  if (!fullName) return '';
  return fullName.trim().split(/\s+/)[0] ?? '';
}

export function prettifyActionType(actionType: string): string {
  return actionType
    .replace(/^86_/, 'eighty_six_')
    .split('_')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function formatMoney(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (opts.compact && Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `${n < 0 ? '-' : ''}$${Math.abs(k).toFixed(Math.abs(k) >= 10 ? 0 : 1)}k`;
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2,
  }).format(n);
}

export function timeAgo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}


function tzOffsetMs(ts: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ts));
  const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second')) - ts;
}

/** "2026-10-12T07:08" typed for a venue in `tz` -> the exact moment as ISO (UTC). Date-only = end of day. */
export function zonedLocalToIso(local: string, tz: string): string {
  const [d, t] = local.split('T');
  const [y, m, day] = d.split('-').map(Number);
  const [hh, mm] = (t ?? '23:59').split(':').map(Number);
  const wall = Date.UTC(y, m - 1, day, hh, mm);
  let ts = wall - tzOffsetMs(wall, tz);
  const second = wall - tzOffsetMs(ts, tz); // correct across daylight-saving changes
  if (second !== ts) ts = second;
  return new Date(ts).toISOString();
}
